import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

import ChevronDownIcon from "@/assets/icons/chevron-down.svg?react";
import { listBusinessSectors } from "@/lib/tci";
import { cn } from "@/lib/utils";

export type ClientSectorSelectProps = {
  value: string | null;
  onChange: (sector: string) => void;
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
};

/**
 * Business sector dropdown matching Figma 512:361 / 511:197.
 */
export function ClientSectorSelect({
  value,
  onChange,
  placeholder = "Select sector",
  className,
  "aria-label": ariaLabel = "Business Sector",
}: ClientSectorSelectProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const sectors = listBusinessSectors();

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleEscape(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (
      event.key === "ArrowDown" ||
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      setOpen(true);
    }
  }

  return (
    <div ref={rootRef} className={cn("relative w-full", className)}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          "flex w-full items-center justify-between border border-sky-400/35 bg-[rgba(11,22,40,0.42)] px-5 py-3 text-left backdrop-blur-[8px]",
          "focus-visible:border-sky-400 focus-visible:outline-none",
          open && "border-sky-400",
        )}
      >
        <span
          className={cn(
            "font-['Inter'] text-sm font-medium leading-5 tracking-[-0.35px]",
            value ? "text-[#f5f3ee]" : "text-white/50",
          )}
        >
          {value ?? placeholder}
        </span>
        <ChevronDownIcon
          aria-hidden
          className={cn(
            "size-4 shrink-0 text-white/80 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open ? (
        <div
          className="absolute left-0 right-0 top-0 z-20 flex max-h-[280px] flex-col overflow-hidden border border-sky-400/35 bg-[rgba(11,22,40,0.95)] backdrop-blur-[8px]"
          role="presentation"
        >
          <div className="shrink-0 border-b border-sky-400/35 bg-[rgba(56,189,248,0.35)] px-5 py-4">
            <p className="font-['Inter'] text-base font-medium leading-5 tracking-[-0.4px] text-white">
              Select sector
            </p>
          </div>
          <ul
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            className={cn(
              "flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-5 py-4",
              "[&::-webkit-scrollbar]:w-1.5",
              "[&::-webkit-scrollbar-track]:bg-transparent",
              "[&::-webkit-scrollbar-thumb]:bg-[rgba(56,189,248,0.42)]",
            )}
          >
            {sectors.map((sector) => {
              const selected = value === sector;
              return (
                <li key={sector} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    className={cn(
                      "w-full text-left font-['Inter'] text-base font-medium leading-5 tracking-[-0.4px] text-white",
                      "hover:text-[#38bdf8] focus-visible:text-[#38bdf8] focus-visible:outline-none",
                      selected && "text-[#38bdf8]",
                    )}
                    onClick={() => {
                      onChange(sector);
                      setOpen(false);
                    }}
                  >
                    {sector}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
